import React from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { GiftIcon, SparklesIcon, HeartIcon, ShoppingBagIcon, UserGroupIcon, ClockIcon } from "@heroicons/react/24/outline"

const Home = () => {
  const { isAuthenticated, createGuestSession, loading } = useAuth()
  const navigate = useNavigate()

  const handleGetStarted = async () => {
    if (isAuthenticated) {
      navigate("/recommendations")
    } else {
      const result = await createGuestSession()
      if (result.success) {
        navigate("/onboarding")
      }
    }
  }

  const features = [
    {
      icon: SparklesIcon,
      title: "AI-Powered Recommendations",
      description: "Our smart algorithm learns your preferences and suggests perfect gifts for any occasion.",
    },
    {
      icon: HeartIcon,
      title: "Personalized for Recipients",
      description: "Tell us about the person you're shopping for, and we'll find gifts they'll love.",
    },
    {
      icon: ShoppingBagIcon,
      title: "Curated Product Selection",
      description: "Browse through carefully selected products from trusted retailers.",
    },
    {
      icon: UserGroupIcon,
      title: "For Every Relationship",
      description: "Whether it's family, friends, or colleagues, find the perfect gift for everyone.",
    },
    {
      icon: ClockIcon,
      title: "Save Time & Stress",
      description: "No more endless browsing. Get personalized suggestions in minutes.",
    },
    {
      icon: GiftIcon,
      title: "All Occasions",
      description: "Birthdays, holidays, graduations, or just because - we've got you covered.",
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Find the Perfect Gift
              <span className="block text-purple-200">Every Time</span>
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-3xl mx-auto">
              Our AI-powered recommendation engine helps you discover thoughtful gifts that your loved ones will actually want and use.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStarted}
                disabled={loading}
                className="bg-white text-purple-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {loading ? "Loading..." : "Get Started Free"}
              </button>
              <Link
                to="/onboarding"
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-purple-700 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Three simple steps to find the perfect gift</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserGroupIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Tell Us About You</h3>
              <p className="text-gray-600">Share your preferences and past gift experiences to help us understand your style.</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Describe the Recipient</h3>
              <p className="text-gray-600">Tell us about the person you're shopping for - their interests, age, and your relationship.</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <GiftIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Get Perfect Recommendations</h3>
              <p className="text-gray-600">Receive personalized gift suggestions with direct links to purchase.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Givvio?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">We make gift-giving effortless and meaningful</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <feature.icon className="h-12 w-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Find the Perfect Gift?</h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">Join thousands of happy gift-givers who never stress about shopping again.</p>
          <button
            onClick={handleGetStarted}
            disabled={loading}
            className="bg-white text-purple-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            {loading ? "Loading..." : "Start Finding Gifts"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Home
